import { KeyboardEvent, memo, MouseEvent, useMemo } from 'react';
import { uniqueId } from './common';
import { ListboxItem } from './ListboxItem';
import { ListboxContext, TListbox } from './ListboxContext';
import { ListboxKeyhandler } from './ListboxKeyhandler';
import { Combobox } from './Combobox';
import { makeStyledComponent } from '@apia/theme';
import { Box } from '@apia/theme';
import { styles } from './listbox';
import { useStore } from './useStore';
import { useShoutSelectionChange } from './useShoutSelectionChange';
import { useIndexedChildren } from './useIndexedChildren';
import { useListboxAutofocus } from './useListboxAutofocus';
import { useUpdateRowCount } from './useUpdateRowCount';
import { useListboxContextValue } from './useListboxContextValue';

export type TOnConfirmSelection = {
  originalEvent: KeyboardEvent;

  selectedElements: HTMLElement[];
  focusedElement: HTMLElement;
};

export type TOnSelectionChange = {
  focusedElement: HTMLElement;
  selectedElements: HTMLElement[];
};

export type TOnClickNode = MouseEvent;

export type { TListbox };

const NoMemoListbox = makeStyledComponent<TListbox>(
  'Listbox',
  'layout.components.listbox',
  styles,
  (props: TListbox) => {
    const storeId = useMemo(() => uniqueId('Listbox'), []);
    const [listboxActions, useListbox, getState, registerCallback] = useStore(
      storeId,
      props,
    );

    useShoutSelectionChange(props.onSelectionChange, registerCallback);
    useUpdateRowCount(props.children, listboxActions);

    const indexedChildren = useIndexedChildren(props.children, listboxActions);
    const { onClick, onFocus } = useListboxAutofocus(storeId, getState);
    const ref = useListbox<HTMLElement>('listbox', null);
    const variant = props.unstyled ? '' : 'layout.components.listbox';
    const contextValue = useListboxContextValue({
      getState,
      id: storeId,
      listboxActions,
      onClickNode: props.onClickNode,
      onConfirmSelection: props.onConfirmSelection,
      onDoubleClickNode: props.onDoubleClickNode,
      onPressEnterOnNode: props.onPressEnterOnNode,
      onPressKeyOnNode: props.onPressKeyOnNode,
      useListbox,
      variant,
    });

    return (
      <ListboxContext.Provider value={contextValue}>
        <ListboxKeyhandler
          className={`listbox ${props.showSearchbox ? 'withSearchbox' : ''} ${
            props.className ?? ''
          }`}
          {...props.ulProps}
        >
          {props.showSearchbox ? (
            <Combobox {...props.ulProps}>{indexedChildren}</Combobox>
          ) : (
            <Box
              ref={ref}
              as="ul"
              role="listbox"
              tabIndex={0}
              onClick={onClick}
              onFocus={onFocus}
              {...props.ulProps}
              className={`listbox__list ${props?.ulProps?.className ?? ''}`}
            >
              {indexedChildren}
            </Box>
          )}
        </ListboxKeyhandler>
      </ListboxContext.Provider>
    );
  },
  true,
);

const Listbox = memo(NoMemoListbox);

export { Listbox, ListboxItem };
